<div class="bai-accounts-root" aria-label="Accounts dashboard">
  @if (loading()) {
    <div class="bai-skeleton" aria-busy="true" aria-label="Loading accounts">
      <div class="bai-skeleton__summary">
        @for (_ of [1, 2, 3]; track $index) {
          <p-skeleton height="2.25rem" />
        }
      </div>
      <div class="bai-skeleton__body">
        <p-skeleton height="20rem" />
        <p-skeleton height="20rem" />
      </div>
    </div>
  } @else if (!state()) {
    <div class="bai-empty" aria-label="No client selected">
      <i class="pi pi-wallet bai-empty__icon" aria-hidden="true"></i>
      <p class="bai-empty__title">No client selected</p>
      <p class="bai-empty__body">Select a client from the left panel to review account operations.</p>
    </div>
  } @else {
    @let selected = selectedRow();

    <header class="bai-headline" aria-label="Accounts context">
      <div>
        <h2 class="bai-headline__title">Accounts</h2>
        <!-- <p class="bai-headline__subtitle">
          Inventory and account-level actions for execution. Financial health summary stays in the Finances tab.
        </p> -->
      </div>
      <!-- <div class="bai-summary" role="list" aria-label="Accounts operational summary">
        <span class="bai-summary__chip" role="listitem">Total accounts: {{ totalAccountCount() }}</span>
        <span class="bai-summary__chip bai-summary__chip--warn" role="listitem">Needs attention: {{ attentionCount() }}</span>
        <span class="bai-summary__chip bai-summary__chip--stale" role="listitem">Stale data: {{ staleCount() }}</span>
      </div> -->
    </header>

    <div class="bai-accounts-layout">
      <section class="bai-card bai-card--inventory" aria-label="Account inventory">
        <div class="bai-card__header">
          <div>
            <h3 class="bai-card__title">Account Inventory</h3>
            <!-- <p class="bai-card__subtitle">Select a row to inspect details and next actions.</p> -->
          </div>
        </div>

        <div class="bai-toolbar" role="toolbar" aria-label="Inventory controls">
          <div class="bai-toolbar__filters" role="group" aria-label="Account filters">
            <bai-filter-chip-group
              [options]="accountFilterOptions"
              [selected]="activeFilter()"
              ariaLabel="Account filters"
              (selectionChange)="onFilterSelection($event)"
            />
          </div>

          <div class="bai-toolbar__search">
            <i class="pi pi-search bai-toolbar__search-icon" aria-hidden="true"></i>
            <input
              type="search"
              [ngModel]="searchQuery()"
              (ngModelChange)="searchQuery.set($event)"
              placeholder="Search account, institution, last 4, type"
              aria-label="Search accounts"
            />
          </div>

          <div class="bai-toolbar__sort">
            <label for="acc-sort-by">Sort</label>
            <select
              id="acc-sort-by"
              [value]="sortBy()"
              (change)="sortBy.set($any($event.target).value)"
              aria-label="Sort accounts"
            >
              <option value="lastUpdated">Last Updated</option>
              <option value="balance">Balance</option>
              <option value="apr">APR</option>
              <option value="utilization">Utilization</option>
              <option value="ytd">YTD Change</option>
            </select>
            <button
              type="button"
              class="bai-icon-btn"
              (click)="sortDir.set(sortDir() === 'desc' ? 'asc' : 'desc')"
              [attr.aria-label]="sortDir() === 'desc' ? 'Sort ascending' : 'Sort descending'"
            >
              <i [class]="sortDir() === 'desc' ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up'" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        @if (inventoryRows().length === 0) {
          <p class="bai-empty-table">No accounts match the current search or filter.</p>
        } @else {
          <div class="bai-table-wrap">
            <table class="bai-accounts-table" aria-label="Account inventory table">
              <thead>
                <tr>
                  <th baiColResize scope="col">Account</th>
                  <th baiColResize scope="col">Institution</th>
                  <th baiColResize scope="col">Type</th>
                  <th baiColResize scope="col" class="text-right bai-cell-nowrap">Balance</th>
                  <th baiColResize scope="col" class="text-right bai-cell-nowrap">APR</th>
                  <th baiColResize scope="col" class="text-right bai-cell-nowrap">Utilization</th>
                  <th baiColResize scope="col" class="text-right bai-cell-nowrap">YTD ($)</th>
                  <th baiColResize scope="col" class="bai-cell-nowrap">Last Updated</th>
                  <th baiColResize scope="col" class="bai-cell-nowrap">Status</th>
                  <th scope="col" class="bai-cell-nowrap">More</th>
                </tr>
              </thead>
              <tbody>
                @for (row of inventoryRows(); track trackRow($index, row)) {
                  <tr
                    class="bai-accounts-table__row"
                    [class.bai-accounts-table__row--selected]="selectedAccountId() === row.id"
                    (click)="selectAccount(row.id)"
                    (keydown.enter)="selectAccount(row.id)"
                    (keydown.space)="selectAccount(row.id); $event.preventDefault()"
                    tabindex="0"
                    role="button"
                    [attr.aria-label]="'Select account ' + row.account.name"
                  >
                    <td>
                      <div class="bai-account-cell">
                        <span class="bai-account-cell__name">{{ row.account.name }}</span>
                        <span class="bai-account-cell__meta">{{ accountKindLabel(row.kind) }} •••• {{ row.account.last4 }}</span>
                      </div>
                    </td>
                    <td>{{ row.account.institution }}</td>
                    <td>{{ categoryLabel(row.account.category) }}</td>
                    <td class="text-right bai-cell-nowrap">{{ formatUsd(row.account.balance) }}</td>
                    <td class="text-right bai-cell-nowrap">
                      @if (isLiability(row.account) && row.account.apr !== null) {
                        {{ formatApr(row.account.apr) }}%
                      } @else {
                        <span class="text-gray-400">—</span>
                      }
                    </td>
                    <td class="text-right bai-cell-nowrap">
                      @if (isLiability(row.account) && row.account.utilization !== null) {
                        {{ formatPct(row.account.utilization) }}
                      } @else {
                        <span class="text-gray-400">—</span>
                      }
                    </td>
                    <td
                      class="text-right bai-cell-nowrap"
                      [class.bai-ytd--positive]="(!isLiability(row.account) && row.account.ytdChangeDollar > 0) || (isLiability(row.account) && row.account.ytdChangeDollar < 0)"
                      [class.bai-ytd--negative]="(!isLiability(row.account) && row.account.ytdChangeDollar < 0) || (isLiability(row.account) && row.account.ytdChangeDollar > 0)"
                    >
                      {{ formatYtdDollar(row.account.ytdChangeDollar) }}
                    </td>
                    <td class="bai-cell-nowrap" [class.text-amber-700]="isStale(row.account.lastUpdated)">
                      {{ row.account.lastUpdated }}
                    </td>
                    <td class="bai-cell-nowrap">
                      <p-tag
                        [value]="rowRiskLabel(row)"
                        [severity]="rowRiskSeverity(row)"
                        styleClass="text-xs"
                      />
                    </td>
                    <td class="bai-cell-nowrap">
                      <details class="bai-acc-kebab" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
                        <summary class="bai-acc-kebab__btn" aria-label="Account options">
                          <i class="pi pi-bars" aria-hidden="true"></i>
                        </summary>
                        <button
                          type="button"
                          class="bai-acc-kebab__action"
                          (click)="addAccountToCopilot(row)"
                        >
                          Add to Copilot
                        </button>
                      </details>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </section>

      <aside class="bai-sidebar" aria-label="Selected account context">
        <section class="bai-card" aria-label="Selected account summary">
          <div class="bai-card__header">
            <div>
              <h3 class="bai-card__title">Selected Account</h3>
              <!-- <p class="bai-card__subtitle">Focused context for current account.</p> -->
            </div>
            @if (selected) {
              <button type="button" class="bai-secondary-btn" (click)="openSelectedAccountDetail()">View details</button>
            }
          </div>

          @if (selected) {
            <div class="bai-detail-grid">
              <span class="bai-detail-grid__label">Account</span>
              <span class="bai-detail-grid__value">{{ selected.account.name }}</span>

              <span class="bai-detail-grid__label">Kind</span>
              <span class="bai-detail-grid__value">{{ accountKindLabel(selected.kind) }}</span>

              <span class="bai-detail-grid__label">Institution</span>
              <span class="bai-detail-grid__value">{{ selected.account.institution }}</span>

              <span class="bai-detail-grid__label">Type</span>
              <span class="bai-detail-grid__value">{{ categoryLabel(selected.account.category) }}</span>

              <span class="bai-detail-grid__label">Balance</span>
              <span class="bai-detail-grid__value">{{ formatUsd(selected.account.balance) }}</span>

              <span class="bai-detail-grid__label">YTD</span>
              <span class="bai-detail-grid__value">{{ formatYtdDollar(selected.account.ytdChangeDollar) }} ({{ formatYtdPct(selected.account.ytdChangePct) }})</span>

              <span class="bai-detail-grid__label">Last Updated</span>
              <span class="bai-detail-grid__value" [class.text-amber-700]="isStale(selected.account.lastUpdated)">
                {{ selected.account.lastUpdated }}
              </span>

              @if (isLiability(selected.account)) {
                <span class="bai-detail-grid__label">APR</span>
                <span class="bai-detail-grid__value">
                  @if (selected.account.apr !== null) {
                    {{ formatApr(selected.account.apr) }}%
                  } @else {
                    —
                  }
                </span>

                <span class="bai-detail-grid__label">Utilization</span>
                <span class="bai-detail-grid__value">
                  @if (selected.account.utilization !== null) {
                    {{ formatPct(selected.account.utilization) }}
                  } @else {
                    —
                  }
                </span>
              }
            </div>
          } @else {
            <p class="bai-empty-table">Select an account to view details.</p>
          }
        </section>

        <!-- <section class="bai-card" aria-label="Account AI insights">
          <div class="bai-card__header">
            <div>
              <h3 class="bai-card__title">AI Account Insights</h3>
              <p class="bai-card__subtitle">Contextual guidance for the selected account.</p>
            </div>
          </div>

          <div class="bai-insights">
            @for (insight of selectedAccountInsights(); track trackInsight($index, insight)) {
              <article class="bai-acc-insight" [class]="insightSeverityClass(insight.severity)">
                <div class="bai-acc-insight__header">
                  <i [class]="insight.icon + ' bai-acc-insight__icon'" aria-hidden="true"></i>
                  <span class="bai-acc-insight__title">{{ insight.title }}</span>
                  <details class="bai-acc-kebab" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
                    <summary class="bai-acc-kebab__btn" aria-label="Account insight options">
                      <i class="pi pi-bars" aria-hidden="true"></i>
                    </summary>
                    <button
                      type="button"
                      class="bai-acc-kebab__action"
                      (click)="addAccountInsightToCopilot(insight, selected)"
                    >
                      Add to Copilot
                    </button>
                  </details>
                  <p-tag
                    [value]="insight.severity === 'danger' ? 'Risk' : insight.severity === 'warn' ? 'Caution' : 'Info'"
                    [severity]="insightTagSeverity(insight.severity)"
                    styleClass="ml-auto text-xs"
                  />
                </div>
                <p class="bai-acc-insight__body">{{ insight.body }}</p>
              </article>
            }
          </div>
        </section> -->

        <section class="bai-card" aria-label="Recommended actions">
          <div class="bai-card__header">
            <div>
              <h3 class="bai-card__title">Recommended Actions</h3>
              <!-- <p class="bai-card__subtitle">Execution checklist tied to this account.</p> -->
            </div>
          </div>

          <ul class="bai-action-list">
            @for (action of selectedAccountActions(); track trackAction($index, action)) {
              <li>
                <i class="pi pi-check-circle" aria-hidden="true"></i>
                <span>{{ action }}</span>
              </li>
            }
          </ul>
        </section>
      </aside>
    </div>
  }
</div>
